import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { adminService, promptSessionService } from '../services/apiService'
import type { BandcampScrapingResult, PromptSession } from '../types'

const statusLabels: Record<number, string> = {
  0: 'Queued',
  1: 'Processing',
  2: 'Testing',
  3: 'Building',
  4: 'Deploying',
  5: 'Ready for Review',
  6: 'Approved',
  7: 'Merging',
  8: 'Merged',
  9: 'Failed',
  10: 'Rejected',
  11: 'Cancelled'
}

const statusColors: Record<number, string> = {
  0: 'bg-gray-100 text-gray-800',
  1: 'bg-blue-100 text-blue-800',
  2: 'bg-blue-100 text-blue-800',
  3: 'bg-blue-100 text-blue-800',
  4: 'bg-blue-100 text-blue-800',
  5: 'bg-green-100 text-green-800',
  6: 'bg-purple-100 text-purple-800',
  7: 'bg-purple-100 text-purple-800',
  8: 'bg-green-100 text-green-800',
  9: 'bg-red-100 text-red-800',
  10: 'bg-orange-100 text-orange-800',
  11: 'bg-gray-100 text-gray-800'
}

export default function AdminDashboard() {
  const [isScrapingBandcamp, setIsScrapingBandcamp] = useState(false)
  const [scrapingResult, setScrapingResult] = useState<BandcampScrapingResult | null>(null)
  const [scrapingError, setScrapingError] = useState<string | null>(null)

  const [promptText, setPromptText] = useState('')
  const [isSubmittingPrompt, setIsSubmittingPrompt] = useState(false)
  const [promptSessions, setPromptSessions] = useState<PromptSession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [expandedSessionId, setExpandedSessionId] = useState<number | null>(null)

  useEffect(() => {
    loadPromptSessions()
  }, [])

  const loadPromptSessions = async () => {
    setIsLoadingSessions(true)
    try {
      const sessions = await promptSessionService.getAll()
      setPromptSessions(sessions)
    } catch (error) {
      console.error('Error loading prompt sessions:', error)
    } finally {
      setIsLoadingSessions(false)
    }
  }

  const handleBandcampScrape = async () => {
    setIsScrapingBandcamp(true)
    setScrapingError(null)
    setScrapingResult(null)

    try {
      const result = await adminService.triggerBandcampScrape()
      setScrapingResult(result)
    } catch (error) {
      console.error('Error triggering Bandcamp scrape:', error)
      setScrapingError(error instanceof Error ? error.message : 'Failed to trigger scraping')
    } finally {
      setIsScrapingBandcamp(false)
    }
  }

  const handleSubmitPrompt = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!promptText.trim()) return

    setIsSubmittingPrompt(true)
    try {
      await promptSessionService.create(promptText)
      setPromptText('')
      await loadPromptSessions()
    } catch (error) {
      console.error('Error submitting prompt:', error)
      alert(error instanceof Error ? error.message : 'Failed to submit prompt')
    } finally {
      setIsSubmittingPrompt(false)
    }
  }

  const handleApprove = async (id: number) => {
    try {
      await promptSessionService.approve(id)
      await loadPromptSessions()
    } catch (error) {
      console.error('Error approving session:', error)
      alert(error instanceof Error ? error.message : 'Failed to approve session')
    }
  }

  const handleReject = async (id: number) => {
    try {
      await promptSessionService.reject(id)
      await loadPromptSessions()
    } catch (error) {
      console.error('Error rejecting session:', error)
      alert(error instanceof Error ? error.message : 'Failed to reject session')
    }
  }

  const handleCancel = async (id: number) => {
    try {
      await promptSessionService.cancel(id)
      await loadPromptSessions()
    } catch (error) {
      console.error('Error cancelling session:', error)
      alert(error instanceof Error ? error.message : 'Failed to cancel session')
    }
  }

  const toggleSessionDetails = (id: number) => {
    setExpandedSessionId(expandedSessionId === id ? null : id)
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>
      <div className="grid grid-cols-1 gap-6">

        {/* Claude AI Agent Section */}
        <div className="border rounded-lg p-6 bg-gradient-to-br from-purple-50 to-blue-50">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <span className="mr-2">🤖</span>
            Claude AI Agent
          </h2>
          <p className="text-gray-600 mb-4">
            Describe what you want to change in the codebase, and Claude will implement it for you!
          </p>

          <form onSubmit={handleSubmitPrompt} className="mb-6">
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Example: Add a new field 'genre' to the Artist model and update the admin UI to allow editing it..."
              className="w-full h-32 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={isSubmittingPrompt}
            />
            <button
              type="submit"
              disabled={isSubmittingPrompt || !promptText.trim()}
              className={`mt-2 px-6 py-2 rounded-lg font-medium ${
                isSubmittingPrompt || !promptText.trim()
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-500'
              }`}
            >
              {isSubmittingPrompt ? 'Submitting...' : 'Submit to Claude'}
            </button>
          </form>

          {/* Prompt Sessions List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg">Recent Sessions</h3>
              <button
                onClick={loadPromptSessions}
                disabled={isLoadingSessions}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                {isLoadingSessions ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {promptSessions.length === 0 ? (
              <p className="text-gray-500 text-sm">No prompt sessions yet. Submit one above!</p>
            ) : (
              <div className="space-y-3">
                {promptSessions.map((session) => (
                  <div key={session.id} className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[session.status]}`}>
                            {statusLabels[session.status]}
                          </span>
                          {session.queuePosition > 0 && (
                            <span className="text-xs text-gray-500">
                              Queue position: {session.queuePosition}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{session.prompt}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Created: {new Date(session.createdAt).toLocaleString()}</span>
                          {session.testUrl && (
                            <a
                              href={session.testUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              View Test Environment
                            </a>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleSessionDetails(session.id)}
                        className="text-sm text-gray-600 hover:text-gray-800 ml-4"
                      >
                        {expandedSessionId === session.id ? '▼' : '▶'}
                      </button>
                    </div>

                    {expandedSessionId === session.id && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        {session.errorMessage && (
                          <div className="bg-red-50 border border-red-200 rounded p-3">
                            <p className="text-sm font-semibold text-red-800">Error:</p>
                            <p className="text-sm text-red-700">{session.errorMessage}</p>
                          </div>
                        )}

                        {session.affectedFiles && session.affectedFiles.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold mb-1">Affected Files ({session.affectedFiles.length}):</p>
                            <ul className="text-xs text-gray-600 list-disc list-inside">
                              {session.affectedFiles.slice(0, 10).map((file, idx) => (
                                <li key={idx}>{file}</li>
                              ))}
                              {session.affectedFiles.length > 10 && (
                                <li>... and {session.affectedFiles.length - 10} more</li>
                              )}
                            </ul>
                          </div>
                        )}

                        {session.hasDatabaseChanges && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                            <p className="text-sm text-yellow-800">⚠️ This session includes database migrations</p>
                          </div>
                        )}

                        {session.logs && session.logs.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold mb-1">Logs:</p>
                            <div className="bg-gray-900 text-gray-100 rounded p-2 text-xs font-mono max-h-48 overflow-y-auto">
                              {session.logs.slice(-20).map((log, idx) => (
                                <div key={idx}>{log}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        {session.status === 5 && ( // Ready for Review
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(session.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500 text-sm"
                            >
                              ✓ Approve & Merge to Production
                            </button>
                            <button
                              onClick={() => handleReject(session.id)}
                              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 text-sm"
                            >
                              ✗ Reject Changes
                            </button>
                          </div>
                        )}

                        {[0, 1, 2, 3, 4].includes(session.status) && ( // Can cancel if queued or in progress
                          <button
                            onClick={() => handleCancel(session.id)}
                            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 text-sm"
                          >
                            Cancel Session
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Manage Artists Section */}
        <div className="border rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Manage Artists</h2>
          <p className="text-gray-600 mb-4">
            Add, edit, or remove artists from your roster
          </p>
          <Link
            to="/admin/artists"
            className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors"
          >
            Manage Artists
          </Link>
        </div>

        {/* Bandcamp Import Section */}
        <div className="border rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Import from Bandcamp</h2>
          <p className="text-gray-600 mb-4">
            Automatically import all releases from your Bandcamp page. This will create new artists and releases or update existing ones.
          </p>
          <button
            onClick={handleBandcampScrape}
            disabled={isScrapingBandcamp}
            className={`px-4 py-2 rounded font-medium ${
              isScrapingBandcamp
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-500'
            }`}
          >
            {isScrapingBandcamp ? 'Importing...' : 'Import from Bandcamp'}
          </button>

          {scrapingResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
              <h3 className="font-bold text-green-800 mb-2">Import Completed!</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>Total releases processed: {scrapingResult.totalReleases}</li>
                <li>New releases added: {scrapingResult.newReleases}</li>
                <li>Existing releases updated: {scrapingResult.updatedReleases}</li>
                {scrapingResult.errors > 0 && (
                  <li className="text-red-700">Errors: {scrapingResult.errors}</li>
                )}
              </ul>
              {scrapingResult.errorMessages.length > 0 && (
                <div className="mt-2">
                  <p className="font-semibold text-red-700">Error details:</p>
                  <ul className="text-xs text-red-600 list-disc list-inside">
                    {scrapingResult.errorMessages.map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {scrapingError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
              <h3 className="font-bold text-red-800 mb-2">Error</h3>
              <p className="text-sm text-red-700">{scrapingError}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
